import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSliderModule } from '@angular/material/slider';
import { map, of, Subscription, switchMap } from 'rxjs';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../../shared-services/environment-config.token';

interface PricingLeverSliderConfig {
  widgetId?: string;
  title?: string;
  highlightText?: string;
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  inputLabel?: string;
  suffix?: string;
  filterKey?: string | string[];
  filterListener?: string | string[];
  filterListner?: string | string[];
  key?: string;
  isQueryParam?: boolean;
  apiUrl?: string;
  method?: string;
  requestFilterKeys?: string[];
  valueAction?: PricingLeverSliderValueAction;
  changeAction?: PricingLeverSliderApiAction;
  commitDebounceMs?: number;
  data?: unknown;
  dataPath?: string;
  valueKey?: string;
  minKey?: string;
  maxKey?: string;
  stepKey?: string;
  deriveBoundsFromValue?: boolean;
  boundOffset?: number;
  boundsOffset?: number;
  inheritApaPricingLever?: boolean;
  apaRangesUrl?: string;
  apaProductTypeKey?: string;
  apaRangeLabelKey?: string;
  apaRangeValueKey?: string;
  height?: string;
  heightSm?: string;
  heightMd?: string;
  heightLg?: string;
}

interface PricingLeverSliderApiAction {
  apiUrl?: string;
  method?: string;
  functionName?: string;
  entity?: string;
  workflowId?: string;
  version?: string;
  headers?: Record<string, string>;
  requestFilterKeys?: string[];
  requestPayload?: Record<string, unknown>;
}

interface PricingLeverSliderValueAction extends PricingLeverSliderApiAction {
  dataPath?: string;
  valueKey?: string;
  targetValueKey?: string;
}

@Component({
  selector: 'app-pricing-lever-slider',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSliderModule],
  templateUrl: './pricing-lever-slider.component.html',
  styleUrls: ['./pricing-lever-slider.component.scss'],
})
export class PricingLeverSliderComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly APA_OVERRIDES_STORAGE_KEY = 'dvm_apa_range_overrides';
  @Input() data?: PricingLeverSliderConfig;
  @Output() valueChange = new EventEmitter<number>();

  min: number | null = null;
  max: number | null = null;
  step: number | null = null;
  value: number | null = null;
  displayValue: number | null = null;
  inputValue: number | null = null;
  private committedValue: number | null = null;
  fontScale = 1;
  errorMessage = '';
  configReady = false;

  private readonly configService = inject(ConfigService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private readonly globalFilterService = inject(GlobalFilterService);
  private readonly responsiveLayoutService = inject(ResponsiveLayoutService);
  private readonly environmentConfig = inject<EnvironmentConfig | null>(ENVIRONMENT_CONFIG, { optional: true });
  private dataSub?: Subscription;
  private changeActionSub?: Subscription;
  private reflowSub?: Subscription;
  private filterSub?: Subscription;
  private commitTimer?: ReturnType<typeof setTimeout>;
  private lastEmittedValue: number | null = null;
  private apaInitialized = false;
  private userHasChangedValue = false;
  private lastResolvedProductType = '';
  private lastConfigRecord: Record<string, unknown> | null = null;

  ngOnInit(): void {
    this.applyResponsiveSizing();
    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => this.applyResponsiveSizing());
    this.filterSub = this.globalFilterService.filter$.subscribe(() => this.handleFilterDrivenInitialization());
    this.loadConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.applyResponsiveSizing();
      this.loadConfig();
    }
  }

  ngOnDestroy(): void {
    this.dataSub?.unsubscribe();
    this.changeActionSub?.unsubscribe();
    this.clearCommitTimer();
    this.reflowSub?.unsubscribe();
    this.filterSub?.unsubscribe();
  }

  @HostBinding('style.--pricing-slider-font-scale')
  get hostFontScale(): string {
    return this.fontScale.toString();
  }

  get suffix(): string {
    return this.data?.suffix ?? '%';
  }

  get formattedMin(): string {
    return this.formatPercent(this.min ?? 0);
  }

  get formattedMax(): string {
    return this.formatPercent(this.max ?? 0);
  }

  get formattedValue(): string {
    const value = this.displayValue ?? this.value;
    return value === null ? '' : this.formatPercent(value);
  }

  get userInputEnabled(): boolean {
    return this.resolveBooleanFlag(this.environmentConfig?.USER_INPUT_ENABLED) ?? true;
  }

  onSliderInput(nextValue: number): void {
    if (!this.userInputEnabled) {
      return;
    }

    this.commitValue(nextValue);
  }

  onInputChange(): void {
    if (!this.userInputEnabled) {
      return;
    }

    this.commitValue(this.inputValue);
  }

  private loadConfig(): void {
    this.dataSub?.unsubscribe();
    this.errorMessage = '';
    const currentFilters = this.globalFilterService.getCurrentFilters() || {};
    const loadFilters = this.selectLoadFilters(currentFilters);

    if (!this.data?.apiUrl) {
      this.applyConfig(this.applyApaPricingLeverToSource(this.data));
      return;
    }

    this.dataSub = this.configService.getConfig(this.data.apiUrl, this.data, loadFilters, this.data.method).pipe(
      switchMap((response) => {
        if (!this.data?.inheritApaPricingLever) {
          return of(response);
        }

        const apaUrl = String(
          this.data?.apaRangesUrl || 'assets/mock-data/dvm/account-classification/apa-ranges.json',
        ).trim();

        if (!apaUrl) {
          return of(response);
        }

        return this.configService.getData(apaUrl).pipe(
          map((apaResponse) => this.applyApaPricingLeverToSource(response, apaResponse)),
        );
      }),
      switchMap((response) => this.loadConfiguredValueAction(response, currentFilters)),
    ).subscribe({
      next: (response) => this.applyConfig(response),
      error: () => {
        this.errorMessage = 'Unable to load pricing lever data.';
        this.applyConfig(this.applyApaPricingLeverToSource(this.data));
      },
    });
  }

  private loadConfiguredValueAction(rangeSource: unknown, currentFilters: any) {
    const valueAction = this.data?.valueAction;
    if (!valueAction?.apiUrl) {
      return of(rangeSource);
    }

    const actionFilters = this.selectActionLoadFilters(valueAction, currentFilters, rangeSource);

    return this.configService
      .getConfig(valueAction.apiUrl, valueAction, actionFilters, valueAction.method)
      .pipe(map((valueSource) => this.mergeConfiguredValueSource(rangeSource, valueSource, valueAction)));
  }

  private mergeConfiguredValueSource(
    rangeSource: unknown,
    valueSource: unknown,
    valueAction: PricingLeverSliderValueAction,
  ): unknown {
    const rangeRecord = this.resolveConfigRecord(rangeSource);
    const valueRecord = this.resolveValueActionRecord(valueSource, valueAction);
    const valueKey = valueAction.valueKey || this.data?.valueKey || 'value';
    const targetValueKey = valueAction.targetValueKey || this.data?.valueKey || valueKey;
    const value =
      valueRecord?.[valueKey] ??
      this.findValueForKey(valueSource, valueKey);

    if (!rangeRecord || value === undefined || value === null || value === '') {
      return rangeSource;
    }

    return {
      ...rangeRecord,
      [targetValueKey]: value,
    };
  }

  private resolveValueActionRecord(
    source: unknown,
    valueAction: PricingLeverSliderValueAction,
  ): Record<string, unknown> | null {
    const configuredPath = valueAction.dataPath;
    const candidates = [
      configuredPath ? this.resolvePath(source, configuredPath) : undefined,
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'data.0'),
      source,
    ];

    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as Record<string, unknown>;
      }
    }

    return null;
  }

  private applyConfig(source: unknown): void {
    const record = this.resolveConfigRecord(source);
    this.lastConfigRecord = record;

    const valueKey = this.data?.valueKey || 'value';
    const resolvedValue = this.findValueForKey(source, valueKey);
    const value = this.readRequiredNumber(
      record,
      valueKey,
      resolvedValue ?? this.data?.value ?? this.data?.defaultValue,
    );
    const derivedBounds = this.resolveDerivedBounds(value);
    const minKey = this.data?.minKey || 'min';
    const maxKey = this.data?.maxKey || 'max';
    const stepKey = this.data?.stepKey || 'step';
    const minFallback = this.findValueForKey(source, minKey) ?? derivedBounds?.min ?? this.data?.min;
    const maxFallback = this.findValueForKey(source, maxKey) ?? derivedBounds?.max ?? this.data?.max;
    const stepFallback = this.findValueForKey(source, stepKey) ?? derivedBounds?.step ?? this.data?.step;
    const min = this.readRequiredNumber(record, minKey, minFallback);
    const max = this.readRequiredNumber(record, maxKey, maxFallback);
    const step = this.readRequiredNumber(record, stepKey, stepFallback);
    const displayValueKey = (this.data as any)?.displayValueKey;
    const displayValue = displayValueKey
      ? this.readOptionalNumber(
          record,
          displayValueKey,
          this.findValueForKey(source, displayValueKey),
        )
      : value;

    const sliderValue = value ?? displayValue;

    if (min === null || max === null || step === null || sliderValue === null) {
      this.configReady = false;
      this.errorMessage = 'Pricing lever config must provide min, max, step, and value.';
      return;
    }

    this.min = min;
    this.max = max;
    this.step = step;
    this.configReady = true;
    this.setDisplayValue(sliderValue, true);
    this.displayValue = displayValue ?? this.value;
    this.committedValue = this.value;
    this.lastEmittedValue = this.value;
    this.applyInitialFilter();
  }

  private resolveDerivedBounds(value: number | null): { min: number; max: number; step: number } | null {
    if (this.data?.deriveBoundsFromValue !== true || value === null) {
      return null;
    }

    const offset = Number(this.data?.boundOffset ?? this.data?.boundsOffset ?? 10);
    const safeOffset = Number.isFinite(offset) && offset > 0 ? offset : 10;
    const configuredStep = Number(this.data?.step);
    return {
      min: value - safeOffset,
      max: value + safeOffset,
      step: Number.isFinite(configuredStep) && configuredStep > 0 ? configuredStep : 0.1,
    };
  }

  private applyInitialFilter(): void {
    const filterKey = this.data?.filterKey || this.data?.key;
    if (!filterKey || this.value === null) {
      return;
    }

    const filterKeys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
          .split(',')
          .map((key) => key.trim())
          .filter(Boolean);

    if (filterKeys.length === 0) {
      return;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters();
    const hasExisting = filterKeys.some(
      (key) =>
        currentFilters?.requestParams?.[key] !== undefined ||
        currentFilters?.queryParams?.[key] !== undefined,
    );

    if (hasExisting && !this.data?.inheritApaPricingLever) {
      return;
    }

    const isQueryParam = Boolean(this.data?.isQueryParam);
    this.globalFilterService.updateFilterSelection(
      filterKeys,
      [this.value],
      isQueryParam,
      this.data?.widgetId,
      false,
      this.data?.filterListener || this.data?.filterListner,
    );
  }

  private resolveConfigRecord(source: unknown): Record<string, unknown> | null {
    const configuredPath = this.data?.dataPath;
    const candidates = [
      configuredPath ? this.resolvePath(source, configuredPath) : undefined,
      this.resolvePath(source, 'responseList.0.data.0.pricingLever'),
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'data.0.pricingLever'),
      this.resolvePath(source, 'data.0'),
      this.data?.data,
      source,
    ];

    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as Record<string, unknown>;
      }
    }

    return null;
  }

  private commitValue(rawValue: unknown, emit = true): void {
    if (!this.userInputEnabled) {
      return;
    }

    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    this.setDisplayValue(rawValue);

    if (!emit) {
      return;
    }

    this.userHasChangedValue = true;

    if (this.value === null || this.committedValue === this.value || this.lastEmittedValue === this.value) {
      return;
    }

    this.committedValue = this.value;
    this.lastEmittedValue = this.value;
    this.valueChange.emit(this.value);
    const filterKey = this.data?.filterKey || this.data?.key;

    if (filterKey) {
      const filterPayload = this.buildFilterPayload(filterKey, this.value);
      this.scheduleCommit(filterKey, filterPayload);
    }
  }

  private scheduleCommit(filterKey: string | string[], filterPayload: Record<string, number>): void {
    this.clearCommitTimer();

    const commit = () => {
      this.executeChangeAction(filterPayload);
      this.chartFilterHandler.updateFilterSelection(
        { ...this.data, filterKey },
        filterPayload,
        filterPayload,
      );
    };

    const debounceMs = this.resolveCommitDebounceMs();
    if (debounceMs <= 0) {
      commit();
      return;
    }

    this.commitTimer = setTimeout(() => {
      this.commitTimer = undefined;
      commit();
    }, debounceMs);
  }

  private clearCommitTimer(): void {
    if (!this.commitTimer) {
      return;
    }

    clearTimeout(this.commitTimer);
    this.commitTimer = undefined;
  }

  private resolveCommitDebounceMs(): number {
    const raw = Number(this.data?.commitDebounceMs);
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }

  private resolveBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }
      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }

  private setDisplayValue(rawValue: unknown, clampValue = true): void {
    if (this.min === null || this.max === null || this.step === null) {
      return;
    }

    const parsedValue = this.toNumber(rawValue, this.value ?? this.min);
    const nextValue = clampValue ? this.clamp(parsedValue) : parsedValue;
    const decimals = this.resolveValueDecimals();
    this.value = Number(nextValue.toFixed(decimals));
    this.inputValue = this.value;
    this.displayValue = this.value;
  }

  private buildFilterPayload(filterKey: string | string[], value: number): Record<string, number> {
    const keys = Array.isArray(filterKey)
      ? filterKey
      : String(filterKey)
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean);

    return keys.reduce<Record<string, number>>((payload, key) => {
      payload[key] = value;
      return payload;
    }, {});
  }

  private executeChangeAction(requestData: Record<string, number>): void {
    const action = this.data?.changeAction;
    if (!action?.apiUrl) {
      return;
    }

    const actionRequestData = this.buildChangeActionRequestData(action, requestData);
    this.changeActionSub?.unsubscribe();
    const requestConfig = this.withChangedRequestKeys(action, actionRequestData, requestData);
    this.changeActionSub = this.configService.getConfig(
      action.apiUrl,
      requestConfig,
      { requestParams: actionRequestData },
      action.method,
    ).subscribe({
      error: () => {
        this.errorMessage = 'Unable to update pricing lever.';
      },
    });
  }

  private withChangedRequestKeys(
    action: PricingLeverSliderApiAction,
    actionRequestData: Record<string, unknown>,
    changedRequestData: Record<string, number>,
  ): PricingLeverSliderApiAction {
    const configuredKeys = Array.isArray(action.requestFilterKeys) ? action.requestFilterKeys : [];
    const changedKeys = Object.keys(changedRequestData);

    return {
      ...action,
      requestFilterKeys: Array.from(new Set([...configuredKeys, ...changedKeys, ...Object.keys(actionRequestData)])),
    };
  }

  private buildChangeActionRequestData(
    action: PricingLeverSliderApiAction,
    changedRequestData: Record<string, number>,
  ): Record<string, unknown> {
    const actionKeys = action.requestFilterKeys;
    if (!Array.isArray(actionKeys) || actionKeys.length === 0) {
      return changedRequestData;
    }

    const currentFilters = this.globalFilterService.getCurrentFilters() || {};
    const requestData = actionKeys.reduce<Record<string, unknown>>((payload, key) => {
      const value =
        currentFilters?.requestParams?.[key] ??
        currentFilters?.queryParams?.[key] ??
        this.lastConfigRecord?.[key] ??
        this.findValueForKey(this.lastConfigRecord, key);

      if (value !== undefined && value !== null && value !== '') {
        payload[key] = this.normalizeRequestValue(value);
      }

      return payload;
    }, {});

    return {
      ...(action.requestPayload || {}),
      ...requestData,
      ...changedRequestData,
    };
  }

  private normalizeRequestValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.length === 1 ? this.normalizeRequestValue(value[0]) : value.map((item) => this.normalizeRequestValue(item));
    }

    return value;
  }

  private selectLoadFilters(filters: any): any {
    const requestFilterKeys = this.data?.requestFilterKeys;
    if (!Array.isArray(requestFilterKeys) || requestFilterKeys.length === 0) {
      return filters;
    }

    const requestParams = requestFilterKeys.reduce<Record<string, unknown>>((selected, key) => {
      const value = filters?.requestParams?.[key];
      if (value !== undefined) {
        selected[key] = value;
      }
      return selected;
    }, {});

    return { ...filters, requestParams };
  }

  private selectActionLoadFilters(action: PricingLeverSliderApiAction, filters: any, rangeSource?: unknown): any {
    const requestFilterKeys = action.requestFilterKeys;
    if (!Array.isArray(requestFilterKeys) || requestFilterKeys.length === 0) {
      return filters;
    }

    const rangeRecord = this.resolveConfigRecord(rangeSource);
    const requestParams = requestFilterKeys.reduce<Record<string, unknown>>((selected, key) => {
      const value =
        filters?.requestParams?.[key] ??
        filters?.queryParams?.[key] ??
        rangeRecord?.[key] ??
        this.findValueForKey(rangeSource, key) ??
        (action.requestPayload as Record<string, unknown> | undefined)?.[key];
      if (value !== undefined) {
        selected[key] = value;
      }
      return selected;
    }, {});

    return { ...filters, requestParams };
  }

  private applyResponsiveSizing(): void {
    this.fontScale = this.responsiveLayoutService.getFontScale();
  }

  private formatPercent(value: number): string {
    return `${Number(value.toFixed(this.resolveDisplayDecimals())).toString()}${this.suffix}`;
  }

  private resolveValueDecimals(): number {
    const configured = Number(
      (this.data as any)?.valueDecimals ??
      (this.data as any)?.displayDecimals ??
      (this.data as any)?.decimals,
    );

    if (Number.isInteger(configured) && configured >= 0) {
      return Math.min(configured, 10);
    }

    return this.decimalPlaces(this.step ?? 0);
  }

  private resolveDisplayDecimals(): number {
    const configured = Number(
      (this.data as any)?.displayDecimals ??
      (this.data as any)?.valueDecimals ??
      (this.data as any)?.decimals,
    );

    if (Number.isInteger(configured) && configured >= 0) {
      return Math.min(configured, 10);
    }

    return 2;
  }

  private toNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private readRequiredNumber(
    record: Record<string, unknown> | null,
    key: string,
    fallback?: unknown,
  ): number | null {
    const raw = record?.[key] ?? fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private readOptionalNumber(
    record: Record<string, unknown> | null,
    key: string,
    fallback?: unknown,
  ): number | null {
    const raw = record?.[key] ?? fallback;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private clamp(value: number): number {
    if (this.min === null || this.max === null) {
      return value;
    }

    return Math.min(this.max, Math.max(this.min, value));
  }

  private decimalPlaces(value: number): number {
    const text = String(value);
    return text.includes('.') ? text.split('.')[1].length : 0;
  }

  private resolvePath(source: unknown, path: string): unknown {
    if (!path) {
      return undefined;
    }

    return path.split('.').reduce<unknown>((current, segment) => {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (Array.isArray(current)) {
        const index = Number(segment);
        return Number.isInteger(index) ? current[index] : undefined;
      }
      if (typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }
      return undefined;
    }, source);
  }

  private findValueForKey(source: unknown, key: string): unknown {
    if (!key || source === null || source === undefined) {
      return undefined;
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        const value = this.findValueForKey(item, key);
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
      return undefined;
    }

    if (typeof source !== 'object') {
      return undefined;
    }

    const record = source as Record<string, unknown>;
    if (record[key] !== undefined && record[key] !== null && record[key] !== '') {
      return record[key];
    }

    for (const value of Object.values(record)) {
      const nestedValue = this.findValueForKey(value, key);
      if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
        return nestedValue;
      }
    }

    return undefined;
  }

  private applyApaPricingLeverToSource(source: unknown, apaSource?: unknown): unknown {
    if (!this.data?.inheritApaPricingLever) {
      return source;
    }

    const baseRecord = this.resolveConfigRecord(source);
    const productType = this.resolveSelectedProductPriceType(source);
    if (!productType) {
      return baseRecord || source;
    }

    const apaRecord = this.resolveApaRangeRecord(productType, apaSource);
    if (!apaRecord) {
      return baseRecord || source;
    }

    this.lastResolvedProductType = productType;
    this.apaInitialized = true;

    const override = this.resolveApaOverride(apaRecord);
    const min = Number(override?.['min'] ?? apaRecord['min']);
    const max = Number(override?.['max'] ?? apaRecord['max']);
    const configuredValueKey = this.data?.valueKey || 'value';
    const configuredValue =
      baseRecord?.[configuredValueKey] ??
      this.findValueForKey(source, configuredValueKey);
    const value = Number.isFinite(Number(configuredValue))
      ? Number(configuredValue)
      : this.resolveApaValue(apaRecord, override);
    const step = this.resolveApaStep(min, max);

    const mergedRecord = {
      ...(baseRecord && typeof baseRecord === 'object' && !Array.isArray(baseRecord)
        ? baseRecord as Record<string, unknown>
        : {}),
      min,
      max,
      step,
      value,
      product_price_type: productType,
    };

    return mergedRecord;
  }

  private resolveSelectedProductPriceType(source: unknown): string {
    const key = this.data?.apaProductTypeKey || 'product_price_type';
    const currentFilters = this.globalFilterService.getCurrentFilters();
    const fromFilters =
      currentFilters?.requestParams?.[key] ??
      currentFilters?.queryParams?.[key];
    const normalizedFromFilters = Array.isArray(fromFilters) ? fromFilters[0] : fromFilters;
    if (normalizedFromFilters !== undefined && normalizedFromFilters !== null && normalizedFromFilters !== '') {
      return String(normalizedFromFilters);
    }

    const record = this.resolveConfigRecord(source);
    const fromRecord = record?.[key];
    if (fromRecord !== undefined && fromRecord !== null && fromRecord !== '') {
      return String(fromRecord);
    }

    return '';
  }

  private resolveApaRangeRecord(productType: string, apaSource?: unknown): Record<string, unknown> | null {
    const rows = Array.isArray((apaSource as any)?.responseList?.[0]?.data)
      ? (apaSource as any).responseList[0].data
      : Array.isArray((apaSource as any)?.data)
        ? (apaSource as any).data
        : Array.isArray(apaSource)
          ? apaSource
          : [];

    const labelKey = this.data?.apaRangeLabelKey || 'label';
    const normalizedProductType = this.normalizeApaLabel(productType);
    const matched = rows.find((row: any) => this.normalizeApaLabel(row?.[labelKey]) === normalizedProductType);
    return matched && typeof matched === 'object' ? matched as Record<string, unknown> : null;
  }

  private resolveApaValue(apaRecord: Record<string, unknown>, override?: Record<string, unknown>): number {
    const labelKey = this.data?.apaRangeLabelKey || 'label';
    const valueKey = this.data?.apaRangeValueKey || 'value';
    const label = String(apaRecord?.[labelKey] || '');
    const overrides = this.readApaOverridesMap();
    const savedOverride = override ?? this.resolveStoredApaOverride(overrides[this.normalizeApaLabel(label)]);
    const fallback = Number(apaRecord?.['currentValue'] ?? apaRecord?.[valueKey] ?? this.data?.value ?? this.data?.defaultValue ?? 0);
    const nextValue = savedOverride?.['value'] ?? fallback;
    return Number.isFinite(Number(nextValue)) ? Number(nextValue) : 0;
  }

  private resolveApaStep(min: number, max: number): number {
    const range = max - min;
    if (!Number.isFinite(range) || range <= 0) {
      return Number(this.data?.step) || 0.01;
    }

    const suggested = Number((range / 100).toFixed(2));
    return suggested > 0 ? suggested : 0.01;
  }

  private readApaOverridesMap(): Record<string, unknown> {
    try {
      const raw = sessionStorage.getItem(PricingLeverSliderComponent.APA_OVERRIDES_STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private resolveApaOverride(apaRecord: Record<string, unknown>): Record<string, unknown> | undefined {
    const labelKey = this.data?.apaRangeLabelKey || 'label';
    const label = String(apaRecord?.[labelKey] || '');
    const overrides = this.readApaOverridesMap();
    return this.resolveStoredApaOverride(overrides[this.normalizeApaLabel(label)]);
  }

  private resolveStoredApaOverride(override: unknown): Record<string, unknown> | undefined {
    if (override && typeof override === 'object' && !Array.isArray(override)) {
      return override as Record<string, unknown>;
    }

    if (Number.isFinite(Number(override))) {
      return { value: Number(override) };
    }

    return undefined;
  }

  private normalizeApaLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private handleFilterDrivenInitialization(): void {
    if (!this.data?.inheritApaPricingLever || this.userHasChangedValue) {
      return;
    }

    const productType = this.resolveSelectedProductPriceType(this.data);
    if (!productType) {
      return;
    }

    if (this.apaInitialized && this.lastResolvedProductType === productType) {
      return;
    }

    this.loadConfig();
  }
}
